import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DuplicateAnalysis = ({ onClose }) => {
  const [loading, setLoading] = useState(false);
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [analyzed, setAnalyzed] = useState(false);
  const [error, setError] = useState('');
  const [processingGroup, setProcessingGroup] = useState(null);

  const analyzeDuplicates = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await axios.get(`${API}/projects/analyze-duplicates`);
      setDuplicateGroups(response.data.duplicate_groups);
      setAnalyzed(true);
    } catch (err) {
      console.error('Failed to analyze duplicates:', err);
      setError(err.response?.data?.detail || 'Не удалось проанализировать дубли');
    } finally {
      setLoading(false);
    }
  };

  const mergeProjects = async (mainProjectId, duplicateIds) => {
    if (!window.confirm('Вы уверены, что хотите объединить эти проекты? Дубли будут удалены, а их данные объединены с основным проектом.')) {
      return;
    }

    setProcessingGroup(mainProjectId);
    try {
      await axios.post(`${API}/projects/${mainProjectId}/merge`, {
        duplicate_ids: duplicateIds
      });
      
      // Remove merged group from display
      setDuplicateGroups(prev => prev.filter(group => group[0].id !== mainProjectId));
      
      alert('Проекты успешно объединены!');
    } catch (err) {
      console.error('Failed to merge projects:', err);
      setError(err.response?.data?.detail || 'Не удалось объединить проекты');
    } finally {
      setProcessingGroup(null);
    }
  };

  const deleteProject = async (projectId) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот проект? Это действие нельзя отменить.')) {
      return;
    }

    setProcessingGroup(projectId);
    try {
      await axios.delete(`${API}/projects/${projectId}`);
      
      // Remove project from all groups
      setDuplicateGroups(prev => 
        prev.map(group => {
          const filtered = group.filter(p => p.id !== projectId);
          return filtered.length > 1 ? filtered : null;
        }).filter(Boolean)
      );
      
      alert('Проект успешно удален!');
    } catch (err) {
      console.error('Failed to delete project:', err);
      setError(err.response?.data?.detail || 'Не удалось удалить проект');
    } finally {
      setProcessingGroup(null);
    }
  };

  const getColorByScore = (score) => {
    if (score >= 90) return 'bg-red-100 text-red-800 border-red-300';
    if (score >= 80) return 'bg-orange-100 text-orange-800 border-orange-300';
    return 'bg-yellow-100 text-yellow-800 border-yellow-300';
  };

  const getIconByScore = (score) => {
    if (score >= 90) return '🚨';
    if (score >= 80) return '⚠️';
    return '💡';
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content max-w-6xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                <span className="text-2xl">🔍</span>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  Анализ дублей в базе данных
                </h2>
                <p className="text-gray-600">
                  Поиск потенциальных дублированных проектов
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Initial State */}
          {!analyzed && !loading && (
            <div className="text-center py-12">
              <div className="w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl">🔍</span>
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-3">
                Анализ дублей не запущен
              </h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                Нажмите кнопку ниже для анализа всей базы данных на предмет потенциальных дублей проектов
              </p>
              <button
                onClick={analyzeDuplicates}
                className="btn-primary"
              >
                Начать анализ
              </button>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-6"></div>
              <h3 className="text-xl font-medium text-gray-900 mb-3">
                Анализируем базу данных...
              </h3>
              <p className="text-gray-600">
                Это может занять некоторое время в зависимости от количества проектов
              </p>
            </div>
          )}

          {/* Results */}
          {analyzed && !loading && (
            <div>
              {/* Summary */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-center">
                  <span className="text-2xl mr-3">📊</span>
                  <div>
                    <h3 className="text-lg font-medium text-blue-900">
                      Анализ завершен
                    </h3>
                    <p className="text-blue-700">
                      Найдено {duplicateGroups.length} групп потенциальных дублей
                    </p>
                  </div>
                </div>
              </div>

              {/* No Duplicates */}
              {duplicateGroups.length === 0 && (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl">✅</span>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Дубли не найдены
                  </h3>
                  <p className="text-gray-600">
                    В вашей базе данных нет проектов с высокой степенью схожести
                  </p>
                </div>
              )}

              {/* Duplicate Groups */}
              {duplicateGroups.length > 0 && (
                <div className="space-y-6">
                  {duplicateGroups.map((group, groupIndex) => {
                    const mainProject = group[0];
                    const duplicates = group.slice(1);
                    const maxScore = Math.max(...duplicates.map(d => d.similarity_score || 0));

                    return (
                      <div
                        key={groupIndex}
                        className="border rounded-lg p-6 bg-white shadow-sm"
                      >
                        {/* Group Header */}
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center">
                            <span className="text-2xl mr-3">{getIconByScore(maxScore)}</span>
                            <div>
                              <h4 className="text-lg font-medium text-gray-900">
                                Группа дублей #{groupIndex + 1}
                              </h4>
                              <p className="text-sm text-gray-600">
                                {duplicates.length + 1} проектов с схожестью до {maxScore}%
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={`px-3 py-1 text-sm font-medium rounded-full ${getColorByScore(maxScore)}`}>
                              Макс. схожесть: {maxScore}%
                            </span>
                            
                            {/* Action buttons */}
                            <div className="flex gap-2">
                              <button
                                onClick={() => mergeProjects(mainProject.id, duplicates.map(d => d.id))}
                                disabled={processingGroup === mainProject.id}
                                className="px-3 py-1 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {processingGroup === mainProject.id ? 'Объединение...' : '🔗 Объединить'}
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Main Project */}
                        <div className="mb-4">
                          <h5 className="text-sm font-medium text-gray-700 mb-2">Основной проект:</h5>
                          <div className="bg-gray-50 rounded-lg p-4">
                            <div className="flex justify-between items-start mb-2">
                              <h6 className="font-medium text-gray-900">
                                <Link 
                                  to={`/projects/${mainProject.id}`}
                                  className="hover:text-blue-600 transition-colors"
                                  target="_blank"
                                >
                                  {mainProject.name}
                                </Link>
                              </h6>
                            </div>
                            <div className="text-sm text-gray-600 grid grid-cols-2 gap-4">
                              <div>
                                <span className="font-medium">Компания:</span> {mainProject.customer_company || 'Не указана'}
                              </div>
                              <div>
                                <span className="font-medium">Город:</span> {mainProject.city}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Duplicates */}
                        <div>
                          <h5 className="text-sm font-medium text-gray-700 mb-2">Похожие проекты:</h5>
                          <div className="space-y-3">
                            {duplicates.map((duplicate, index) => (
                              <div
                                key={duplicate.id}
                                className="bg-yellow-50 border border-yellow-200 rounded-lg p-4"
                              >
                                <div className="flex justify-between items-start mb-2">
                                  <h6 className="font-medium text-gray-900 flex-1">
                                    <Link 
                                      to={`/projects/${duplicate.id}`}
                                      className="hover:text-blue-600 transition-colors"
                                      target="_blank"
                                    >
                                      {duplicate.name}
                                    </Link>
                                  </h6>
                                  <div className="flex items-center gap-2 ml-3">
                                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getColorByScore(duplicate.similarity_score)}`}>
                                      {duplicate.similarity_score}% схожесть
                                    </span>
                                    <button
                                      onClick={() => deleteProject(duplicate.id)}
                                      disabled={processingGroup === duplicate.id}
                                      className="px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                      title="Удалить проект"
                                    >
                                      {processingGroup === duplicate.id ? '...' : '🗑️'}
                                    </button>
                                  </div>
                                </div>
                                <div className="text-sm text-gray-600 grid grid-cols-2 gap-4">
                                  <div>
                                    <span className="font-medium">Компания:</span> {duplicate.customer_company || 'Не указана'}
                                  </div>
                                  <div>
                                    <span className="font-medium">Город:</span> {duplicate.city}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          {analyzed && (
            <div className="flex justify-between pt-6 border-t border-gray-200 mt-6">
              <button
                onClick={() => {
                  setAnalyzed(false);
                  setDuplicateGroups([]);
                }}
                className="btn-secondary"
              >
                Новый анализ
              </button>
              <button
                onClick={onClose}
                className="btn-primary"
              >
                Закрыть
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DuplicateAnalysis;